/**
 * This file contains all JS that needs to run site-wide
 */

/**
 * Initialize Sentry
 */

import * as Sentry from '@sentry/react'

const getDSNBy = domain =>
  ({
    fertighaus:
      'https://f6d8a9d152d545319d2c698cd24381fc@o1253617.ingest.sentry.io/6593702',
    massivhaus:
      'https://8dd31b59f94f4676a3f7b57e3df92403@o1253617.ingest.sentry.io/4504452192600064',
    bungalow:
      'https://a2b6f4d22b9747d1bfaeb2c675c4498a@o1253617.ingest.sentry.io/4504532013350912',
    einfamilienhaus:
      'https://498a7b6759ce4bd8bf22f8a903f08962@o1253617.ingest.sentry.io/4504532017807360',
    bauen:
      'https://650bdea019a92fc86dbfd760d93a7ac6@o1253617.ingest.us.sentry.io/4507101632004096',
    immowelt:
      'https://b3630c7dd7ce9c68639cc68eb72ab3dd@o1253617.ingest.us.sentry.io/4507106562932736',
    immonet:
      'https://21ae5106c88819483fb9eec592f4c4f7@o1253617.ingest.us.sentry.io/4507106565685248'
  }[domain])

if (DEPLOY_ENV === 'staging' || DEPLOY_ENV === 'production') {
  Sentry.init({
    dsn: getDSNBy(window.__DOMAIN__),
    integrations: [Sentry.browserTracingIntegration()],
    environment: DEPLOY_ENV,
    tracesSampleRate: 0.3,
    sampleRate: 0.3,
    ignoreErrors: ['Attempted to assign to readonly property']
  })
}

/**
 * Load AB testing
 */
import { get } from '@js/common/api'
import { supportsLocalStorage } from '@js/common/utils'

if (supportsLocalStorage()) {
  try {
    const LS = JSON.parse(localStorage.getItem('ab-testing') || '{}')
    const planoutId = window['🍪'].read('planout_id')
      ? window['🍪'].read('planout_id')
      : 'user-has-cookies-blocked'

    if (
      !LS.version ||
      (window.SherlockContainer?.environment?.AB_TEST_VERSION &&
        LS.version !== window.SherlockContainer.environment.AB_TEST_VERSION)
    ) {
      get(`/ab-test/${planoutId}`).then(response => {
        localStorage.setItem('ab-testing', JSON.stringify(response))
      })
    }
  } catch (e) {}
}

/**
  Load Google Maps API – callback parameter is required but we're not using it.
 */

void (function () {
  if (document.getElementById('google-maps-api')) {
    return
  }
  const script = document.createElement('script')
  window.initMap = function initMap() {
    return
  }

  const isOldSafariBrowser =
    /^((?!chrome|android).)*safari/i.test(navigator.userAgent) &&
    parseInt((navigator.userAgent.match(/Version\/(\d+)/) || [])[1]) <= 15
  script.src = `https://maps.googleapis.com/maps/api/js?key=${
    window.window.SherlockContainer.environment.GM_KEY
  }${
    isOldSafariBrowser ? '&v=3.55' : ''
  }&language=de&libraries=places,marker&loading=async&callback=initMap`
  script.async = true
  script.id = 'google-maps-api'
  document.body.appendChild(script)
})()

const isIMSite = /immowelt|immonet|bauen/.test(window.__DOMAIN__)

if ('serviceWorker' in navigator && !isIMSite) {
  window.addEventListener('load', function () {
    navigator.serviceWorker
      .register(
        `/service-worker.js?js-files=${encodeURIComponent(
          window.CACHEABLE_JS_FILES.join(',')
        )}`
      )
      .then(function (registration) {
        console.log(`registration successful with scope: ${registration.scope}`)
      }, errorLogger)
      .catch(errorLogger)
  })
}

/**
 * TRACKING
 */
void (function () {
  const htmlCTAs = document.querySelectorAll('.js-cta-click')

  for (const htmlCTA of htmlCTAs) {
    let wtData = {}
    try {
      wtData = JSON.parse(htmlCTA.dataset.ctaClick)
    } catch (e) {}

    htmlCTA.addEventListener('click', function (event) {
      const target = event.target.closest('.js-cta-click')

      window._wt({
        category: `CTA-${target.href ? 'link' : 'button'}`,
        action: 'click',
        values: target.href
          ? [
              {
                type: 'url',
                value: /http|mailto/.test(target.href)
                  ? target.href
                  : `${window.location.origin}${target.href}`
              },
              {
                type: 'text',
                value: target.textContent.trim()
              }
            ]
          : [
              {
                type: 'text',
                value: target.textContent.trim()
              }
            ],
        ...(wtData['CTA-click'] ? wtData['CTA-click'] : {})
      })
    })
  }
})()

/**
 * Load Cookie Modal
 */

import React from 'react'

import Banner from '@f/components/Banner'
import CookieBanner from '@f/widgets/CookieBanner'

import { errorLogger } from '@f-fertighaus/utils/errorLogger'

void (() => {
  const cookieBannerDisabled = window.SherlockContainer.environment.FEATURE_FLAGS.includes(
    '@FF_DISABLE_COOKIE_BANNER'
  )
  if (
    window.location.pathname === '/wuestenrot/' ||
    window.location.pathname === '/wuestenrot-v2/' ||
    cookieBannerDisabled
  )
    return

  const banner = new Banner()

  const cookiePolicyConfig = window['🍪'].read('cookie_policy_config')
  const isCookieBannerHashParamPresent = window.location.hash.includes(
    'm=cookies-einstellungen'
  )

  if (!cookiePolicyConfig || isCookieBannerHashParamPresent) {
    banner.openBanner({
      component: <CookieBanner />
    })
  }

  window.addEventListener('hashchange', function () {
    if (window.location.hash.includes('m=cookies-einstellungen'))
      banner.openBanner({
        component: <CookieBanner />
      })
  })
})()

/**
 * Image Lazy-load check
 */

void (() => {
  const imageElements = document.querySelectorAll('.js-lazy-load')

  const preloadImage = image => {
    if (image.dataset.srcset) image.srcset = image.dataset.srcset
    image.src = image.dataset.src
  }

  const handleObservedImages = images => {
    for (const image of images) {
      if (image.intersectionRatio > 0) {
        observer.unobserve(image.target)
        preloadImage(image.target)
      }
    }
  }

  let observer
  if ('IntersectionObserver' in window) {
    observer = new IntersectionObserver(handleObservedImages, {
      rootMargin: '150px 0px',
      threshold: 0.01
    })
  }

  for (const image of imageElements) {
    if (observer) {
      observer.observe(image)
    } else {
      preloadImage(image)
    }
  }
})()

/**
 * CMS Rich Text link styles update
 */

void (function removeUnderlinesFromStandaloneLinksInListItems() {
  const listItems = document.querySelectorAll('.RichContent-block li')

  listItems.forEach(listItem => {
    const linksContained = listItem.querySelectorAll('a')
    const hasExactlyOneLink = linksContained.length === 1

    if (hasExactlyOneLink) {
      const link = linksContained[0]
      const listItemContent = listItem.textContent.trim()
      const linkContent = link.textContent.trim()

      if (listItemContent === linkContent) {
        link.style.textDecoration = 'none'
      }
    }
  })
})()
